/*
 * product-price-contributions.js — live "price transparency" engine.
 *
 * Computes, per option axis (Metal / Stone / Shape / Carat / Size …),
 * the $ CONTRIBUTION of EVERY value toward the RRP, straight from the
 * variant matrix — never from a hand-maintained price table.
 *
 * Why JS and not Liquid: Liquid cannot resolve per-value prices on
 * high-variant products (the 250-variant serialisation cap) nor across
 * compound options (": in the name, / in the values"), and any stored
 * absolute $ breaks under Shopify Markets. Variant prices arrive in the
 * shopper's presentment currency (embedded JSON + ?view=variants), so
 * every delta computed here is market-correct by construction.
 *
 * Contribution model (waterfall, sums exactly to RRP):
 *   base config    = the product's cheapest variant (its option values
 *                    define the $0 anchor of every axis).
 *   Metal          = metal_base_pct × base-config price   (combined-
 *                    listing group; pct metafield, fallback 1.0 = metal
 *                    absorbs the whole base price).
 *   first axis     = P(value, rest at base) − Metal
 *   later axis k   = P(axes<k current, k=value, axes>k base)
 *                  − P(axes<k current, k=base,  axes>k base)
 *   Σ contributions = current variant price. Exact for any matrix —
 *   no additivity assumption.
 *
 * NO ±$ deltas anywhere: hover previews the same absolute contribution
 * that commits on select, so hover and committed figures always agree.
 * (A raw price delta measures a different thing — the total-price change
 * of switching one axis — and cannot reconcile with waterfall
 * contributions on a non-additive matrix; per client, contributions are
 * the only figure shown.)
 *
 * Total carat weight is painted on axes flagged `data-option-show-tcw`.
 * It is the selected variant's `custom.total_carat_weight`, stored and
 * transported as a string so decimal formatting remains verbatim.
 *
 * DOM contract (written, not read, by this module):
 *   [data-option-value-price]  absolute contribution (hover + commit)
 *   .product-option-accordion__row-price   visible list-row price
 *   [data-option-summary-price="<axis>"]   selected contribution
 *   .product-option-accordion__row-tcw     visible list-row TCW
 *   [data-option-summary-tcw="<axis>"]     selected variant's TCW
 * Metal (product-group-selector):
 *   [data-current-price]        current metal's contribution
 *   a[data-sibling-handle]      dataset.siblingContribution = that
 *                               sibling's own metal contribution
 *                               (hover + commit on switch).
 *
 * Recompute triggers: DOM ready, `product-variants:loaded` (full matrix
 * landed), `variant:update` (selection change or metal switch). All
 * writes are idempotent; SSR static-map prices remain as the value
 * shown until the first compute lands (and as the no-JS fallback).
 */

import { formatMoney } from '@theme/money-formatting';

const ROOT_SELECTOR = '.product-purchase-form';
const INSTANCE_KEY = '_priceContributionsInstance';
const KEY_SEP = '\u0000';

function optionValueOf(variant, position) {
  if (window.ProductVariantMatrix?.optionValue) {
    return window.ProductVariantMatrix.optionValue(variant, position);
  }
  return variant?.['option' + position] ?? null;
}

/** Split a combined compound value; the LAST part absorbs the remainder
 *  (mirrors product-compound-options-accordion.js splitValue). */
function splitCompound(value, sep, partCount) {
  const segs = String(value ?? '').split(sep);
  const out = [];
  for (let i = 0; i < partCount; i++) {
    if (i < partCount - 1) out.push((segs[i] || '').trim());
    else out.push(segs.slice(partCount - 1).join(sep).trim());
  }
  return out;
}

class PriceContributions {
  constructor(root) {
    this.root = root;
    this.moneyFormat = root.dataset.moneyFormat || '${{amount}}';
    this.currency = root.dataset.currency || 'USD';
    this._index = null;
    this._indexStamp = '';
    this._scheduled = false;
    this._tcwById = this._readTcwMap();

    const recompute = () => this.schedule();
    root.addEventListener('variant:update', recompute);
    document.addEventListener('product-variants:loaded', recompute);
    this.schedule();
  }

  /* ---------------- formatting ---------------- */

  _fmt(cents) {
    const text = formatMoney(Math.round(cents), this.moneyFormat, this.currency);
    // money_without_trailing_zeros semantics — "$1,400" not "$1,400.00".
    return text.replace(/([.,]00)(?=\s|$)/, '');
  }

  /* ---------------- product + matrix ---------------- */

  _productJson() {
    const node = this.root.querySelector('.product-purchase-form__product-json');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (_e) {
      return null;
    }
  }

  _keyFor(options) {
    return options.join(KEY_SEP);
  }

  _readTcwMap() {
    const node = this.root.querySelector('.product-purchase-form__tcw-map');
    if (!node?.textContent) return {};
    try {
      return JSON.parse(node.textContent) || {};
    } catch (_e) {
      return {};
    }
  }

  _tcwOf(variant) {
    const own = variant?.tcw;
    if (own != null && own !== '') return String(own);
    const boot = this._tcwById[String(variant?.id)];
    return boot == null || boot === '' ? '' : String(boot);
  }

  /** price index over a variants array: option-values key → cents. */
  _buildIndex(variants, optionCount) {
    const index = new Map();
    const tcwIndex = new Map();
    let cheapest = null;
    variants.forEach((v) => {
      const opts = [];
      for (let p = 1; p <= optionCount; p++) opts.push(optionValueOf(v, p) ?? '');
      const key = this._keyFor(opts);
      index.set(key, Number(v.price));
      const tcw = this._tcwOf(v);
      if (tcw !== '') tcwIndex.set(key, tcw);
      if (!cheapest || Number(v.price) < Number(cheapest.price)) cheapest = v;
    });
    return { index, tcwIndex, cheapest };
  }

  _ensureIndex(product) {
    const variants = product.variants || [];
    const stamp = String(product.id) + ':' + variants.length;
    if (this._index && this._indexStamp === stamp) return this._index;
    const optionCount = (product.options || []).length;
    this._index = this._buildIndex(variants, optionCount);
    this._indexStamp = stamp;
    return this._index;
  }

  /* ---------------- axes ---------------- */

  /**
   * Frontend axes in display (position, then part) order.
   * Plain option  → one axis keyed by the option name.
   * Compound option (":" name + [data-compound-accordion]) → one axis
   * per part, keyed by the trimmed part name.
   */
  _axes(product) {
    const names = product.options || [];
    const axes = [];
    names.forEach((name, i) => {
      const position = i + 1;
      const group = this.root.querySelector(
        `[data-compound-accordion][data-compound-position="${position}"]`
      );
      if (String(name).indexOf(':') !== -1 && group) {
        const parts = String(name).split(':').map((s) => s.trim());
        const sep = group.getAttribute('data-compound-sep') || ' / ';
        parts.forEach((partName, partIdx) => {
          axes.push({ name: partName, position, partIdx, partCount: parts.length, sep, group });
        });
      } else {
        axes.push({ name: String(name), position, partIdx: -1, partCount: 1, sep: null, group: null });
      }
    });
    return axes;
  }

  /** Current value per axis, read from the live inputs. */
  _currentAxisValues(axes) {
    return axes.map((axis) => {
      if (axis.partIdx === -1) {
        const checked = this.root.querySelector(
          `input[data-option-select][data-option-position="${axis.position}"]:checked`
        );
        if (checked) return checked.value;
        const select = this.root.querySelector(
          `select[data-option-select][data-option-position="${axis.position}"]`
        );
        return select ? select.value : null;
      }
      const radio = axis.group.querySelector(
        `input[data-compound-part="${axis.partIdx + 1}"]:checked`
      );
      if (radio) return radio.getAttribute('data-compound-value');
      // Fall back to the hidden combined select.
      const hidden = axis.group.querySelector('select[data-option-select]');
      return hidden
        ? splitCompound(hidden.value, axis.sep, axis.partCount)[axis.partIdx]
        : null;
    });
  }

  /** Base (anchor) value per axis — the cheapest variant's values. */
  _baseAxisValues(axes, cheapest, optionCount) {
    const perPosition = [];
    for (let p = 1; p <= optionCount; p++) perPosition.push(optionValueOf(cheapest, p) ?? '');
    return axes.map((axis) => {
      const combined = perPosition[axis.position - 1];
      if (axis.partIdx === -1) return combined;
      return splitCompound(combined, axis.sep, axis.partCount)[axis.partIdx];
    });
  }

  /** Assemble a per-position option array from per-axis values. */
  _optionsFromAxes(axes, axisValues, optionCount) {
    const out = new Array(optionCount).fill('');
    for (let p = 1; p <= optionCount; p++) {
      const positionAxes = [];
      axes.forEach((axis, i) => {
        if (axis.position === p) positionAxes.push(axisValues[i]);
      });
      if (!positionAxes.length) continue;
      const sep = axes.find((a) => a.position === p)?.sep;
      out[p - 1] = positionAxes.length === 1 ? positionAxes[0] : positionAxes.join(sep);
    }
    return out;
  }

  _price(index, axes, axisValues, optionCount) {
    if (axisValues.some((v) => v == null || v === '')) return null;
    const key = this._keyFor(this._optionsFromAxes(axes, axisValues, optionCount));
    const price = index.get(key);
    return price == null ? null : price;
  }

  _tcwAt(tcwIndex, axes, axisValues, optionCount) {
    if (axisValues.some((v) => v == null || v === '')) return '';
    const key = this._keyFor(this._optionsFromAxes(axes, axisValues, optionCount));
    return tcwIndex.get(key) ?? '';
  }

  /* ---------------- metal group ---------------- */

  _metalComponent() {
    return document.querySelector('product-group-selector-component');
  }

  _pctOf(el, fallback) {
    const raw = el?.dataset?.metalBasePct ?? el?.dataset?.siblingBasePct;
    const pct = parseFloat(raw);
    return Number.isFinite(pct) && pct > 0 && pct <= 1 ? pct : fallback;
  }

  /* ---------------- value elements per axis ---------------- */

  _axisValueElements(axis) {
    const details = this.root.querySelector(
      `details[data-option-accordion="${CSS.escape(axis.name)}"]`
    );
    if (!details) return { details: null, items: [] };
    const items = [];
    if (axis.partIdx === -1) {
      details.querySelectorAll('[data-option-value-label]').forEach((label) => {
        const input = label.previousElementSibling;
        const value = input?.value ?? label.dataset.optionValueLabel;
        items.push({ label, value });
      });
    } else {
      details
        .querySelectorAll(`input[data-compound-part="${axis.partIdx + 1}"]`)
        .forEach((input) => {
          const label = input.nextElementSibling;
          if (!label) return;
          items.push({ label, value: input.getAttribute('data-compound-value') });
        });
    }
    return { details, items };
  }

  _writeItem(item, contributionText) {
    const { label } = item;
    label.dataset.optionValuePrice = contributionText;
    if (label.classList.contains('product-option-accordion__row')) {
      let priceEl = label.querySelector('.product-option-accordion__row-price');
      if (!priceEl) {
        priceEl = document.createElement('span');
        priceEl.className = 'product-option-accordion__row-price';
        const meta = label.querySelector('.product-option-accordion__row-meta');
        (meta || label).appendChild(priceEl);
      }
      priceEl.textContent = contributionText;
    }
  }

  _paintTcw(scope, tcwSelector, sepSelector, text) {
    const tcwEl = scope.querySelector(tcwSelector);
    if (tcwEl) {
      tcwEl.textContent = text;
      tcwEl.hidden = text === '';
    }
    const sepEl = scope.querySelector(sepSelector);
    if (sepEl) sepEl.hidden = text === '';
  }

  _writeItemTcw(item, format, tcwRaw) {
    const text = tcwRaw === '' ? '' : format.replace('{value}', tcwRaw);
    item.label.dataset.optionValueTcw = tcwRaw;
    const tcwEl = item.label.querySelector('.product-option-accordion__row-tcw');
    if (tcwEl) {
      tcwEl.textContent = text;
      tcwEl.hidden = text === '';
    }
    const sepEl = item.label.querySelector('.product-option-accordion__row-sep');
    if (sepEl) {
      const price = item.label.querySelector('.product-option-accordion__row-price');
      sepEl.hidden = text === '' || !price?.textContent?.trim();
    }
    return text;
  }

  /* ---------------- main compute ---------------- */

  schedule() {
    if (this._scheduled) return;
    this._scheduled = true;
    requestAnimationFrame(() => {
      this._scheduled = false;
      try {
        this.compute();
      } catch (_e) {
        /* never break the PDP over pricing text */
      }
    });
  }

  compute() {
    this._tcwById = this._readTcwMap();
    const product = this._productJson();
    if (!product || !(product.variants || []).length) return;
    const optionCount = (product.options || []).length;
    const { index, tcwIndex, cheapest } = this._ensureIndex(product);
    if (!cheapest) return;

    const axes = this._axes(product);
    const current = this._currentAxisValues(axes);
    const base = this._baseAxisValues(axes, cheapest, optionCount);
    const basePrice = Number(cheapest.price);

    const metal = this._metalComponent();
    const activeAnchor = metal?.querySelector('a[data-sibling-handle][aria-current="true"]');
    const pct = this._pctOf(activeAnchor, this._pctOf(metal, 1));
    // The first axis absorbs whatever share of the base price the metal
    // doesn't claim; with no metal group the base price stays on axis 0.
    const axis0Anchor = metal ? pct * basePrice : 0;

    axes.forEach((axis, k) => {
      const { details, items } = this._axisValueElements(axis);
      if (!details || !items.length) return;

      // Anchor: this axis at base, earlier axes current, later axes base.
      let anchor;
      if (k === 0) {
        anchor = axis0Anchor;
      } else {
        const anchorValues = current.map((cv, i) => (i < k ? cv : base[i]));
        anchor = this._price(index, axes, anchorValues, optionCount);
      }
      if (anchor == null) return;

      const showTcw = details.hasAttribute('data-option-show-tcw');
      const tcwFormat = details.getAttribute('data-option-tcw-format') || 'TCW: {value} ct';
      let selectedContribution = null;
      let selectedTcwText = null;
      items.forEach((item) => {
        const contribValues = current.map((cv, i) => {
          if (i === k) return item.value;
          return i < k ? cv : base[i];
        });
        const contribPrice = this._price(index, axes, contribValues, optionCount);
        if (contribPrice != null) {
          const contribution = contribPrice - anchor;
          const contributionText = this._fmt(contribution);
          if (item.value === current[k]) selectedContribution = contributionText;
          this._writeItem(item, contributionText);
        }

        if (showTcw) {
          const tcwValues = current.map((cv, i) => (i === k ? item.value : cv));
          const tcwRaw = this._tcwAt(tcwIndex, axes, tcwValues, optionCount);
          const tcwText = this._writeItemTcw(item, tcwFormat, tcwRaw);
          if (item.value === current[k]) selectedTcwText = tcwText;
        }
      });

      if (selectedContribution != null) {
        const summaryPrice = details.querySelector(
          `[data-option-summary-price="${CSS.escape(axis.name)}"]`
        );
        if (summaryPrice) summaryPrice.textContent = selectedContribution;
      }

      if (showTcw) {
        this._paintTcw(
          details,
          `[data-option-summary-tcw="${CSS.escape(axis.name)}"]`,
          `[data-option-summary-tcw-sep="${CSS.escape(axis.name)}"]`,
          selectedTcwText ?? ''
        );
      }
    });

    if (metal) this._computeMetal(metal, { optionCount, basePrice, pct });
  }

  /**
   * Metal group — contribution for the CURRENT metal, plus each sibling
   * metal's own contribution (pct × its cheapest-variant price), shown
   * on hover and committed on switch. Sibling matrices are warmed on
   * idle by product-group-selector.js; loadAll() below just awaits the
   * cache.
   */
  _computeMetal(metal, { optionCount, basePrice, pct }) {
    const contributionText = this._fmt(pct * basePrice);
    const priceEl = metal.querySelector('[data-current-price]');
    if (priceEl) priceEl.textContent = contributionText;
    if (typeof metal.setCommittedPrice === 'function') {
      metal.setCommittedPrice(contributionText);
    }

    const matrixApi = window.ProductVariantMatrix;
    if (!matrixApi?.loadAll) return;

    const expected = this._indexStamp;

    metal.querySelectorAll('a[data-sibling-handle]').forEach((anchor) => {
      const isActive = anchor.getAttribute('aria-current') === 'true';
      if (isActive) {
        anchor.dataset.siblingContribution = contributionText;
        return;
      }
      const handle = anchor.dataset.siblingHandle;
      if (!handle) return;
      matrixApi
        .loadAll(handle)
        .then((result) => {
          // A metal switch may have landed while this was in flight.
          if (this._indexStamp !== expected) return;
          const variants = matrixApi.normalizeAll(result.variants, optionCount);
          const sib = this._buildIndex(variants, optionCount);
          const sibPct = this._pctOf(anchor, pct);
          anchor.dataset.siblingContribution = this._fmt(sibPct * Number(sib.cheapest.price));
        })
        .catch(() => {});
    });
  }
}

function initAll(root) {
  const scope = root || document;
  scope.querySelectorAll(ROOT_SELECTOR).forEach((el) => {
    if (el[INSTANCE_KEY]) return;
    el[INSTANCE_KEY] = new PriceContributions(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll());
} else {
  initAll();
}

document.addEventListener('shopify:section:load', (e) => initAll(e.target));
